import {
  Button,
  CloseIcon,
  JoonUIColor,
  SegmentedControl,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import GiftItem from "./GiftItem"
import {
  InventoryGiftShopModalTab,
  useInventoryGiftShopModalStore,
} from "./hooks/useInventoryGiftShopModalStore"
import { useSendMailModalStore } from "./hooks/useSendMailModalStore"
import ShopItem from "./ShopItem"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import {
  useInventoryGiftsQuery,
  useUserQuery,
} from "../../../../networking/queries"
import { usePurchaseShopItemMutation } from "../../../../networking/rewards/mutations"
import { useParentShopQuery } from "../../../../networking/rewards/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../../util/analytics"

const InventoryGiftShopModal = () => {
  const { isOpen, onClose, selectedTab, setSelectedTab } =
    useInventoryGiftShopModalStore()
  const { setSelectedGift } = useSendMailModalStore()
  const { t } = useTranslation()

  const onCancel = () => {
    setSelectedGift(null)
    onClose()
  }

  const tabOptions = [
    {
      label: t("MAIL_MY_INVENTORY"),
      value: InventoryGiftShopModalTab.INVENTORY,
    },
    { label: t("MAIL_GIFT_SHOP"), value: InventoryGiftShopModalTab.GIFT_SHOP },
  ]

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onCancel}
      showCard={true}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1200}
    >
      <button className="absolute right-4 top-4 p-2" onClick={onCancel}>
        <CloseIcon color={JoonUIColor.icon.neutral} />
      </button>
      <div className="p-6 pt-10 pb-4">
        <SegmentedControl
          options={tabOptions}
          value={selectedTab}
          setValue={(value) =>
            setSelectedTab(value as InventoryGiftShopModalTab)
          }
          fullWidth
        />
      </div>
      {selectedTab === InventoryGiftShopModalTab.INVENTORY && <InventoryTab />}
      {selectedTab === InventoryGiftShopModalTab.GIFT_SHOP && <GiftShopTab />}
    </SlideUpModal>
  )
}

const InventoryTab = () => {
  const { data: gifts } = useInventoryGiftsQuery()
  const { selectedGift } = useSendMailModalStore()
  const { onClose } = useInventoryGiftShopModalStore()
  const { isOpen: isOpenSendMessageModal, onOpen: openSendMessageModal } =
    useSendMailModalStore()
  const { t } = useTranslation()

  const handleAddToMail = () => {
    if (!isOpenSendMessageModal) openSendMessageModal()
    onClose()
  }

  return (
    <>
      <div className="flex flex-col gap-4 p-6 pt-0 overflow-y-auto">
        {gifts && gifts.length > 0 ? (
          <div className="flex flex-col gap-4">
            <Typography variant="bodySmall">{t("MAIL_SELECT_GIFT")}</Typography>
            <div className="flex flex-col gap-2 overflow-y-auto">
              {gifts?.map((gift) => <GiftItem key={gift.id} gift={gift} />)}
            </div>
          </div>
        ) : (
          <div className="flex flex-row items-center justify-center w-full p-4">
            <Typography
              variant="bodySmall"
              color={JoonUIColor.text.secondary}
              textAlign="center"
            >
              {t("MAIL_HEAD_TO_SHOP")}
            </Typography>
          </div>
        )}
      </div>
      <div className="w-full px-6 py-4 border-t border-borderPrimary bg-white">
        <Button
          text={
            isOpenSendMessageModal
              ? t("MAIL_ADD_TO_MAIL")
              : t("MAIL_ADD_TO_NEW_MAIL")
          }
          onClick={handleAddToMail}
          fullWidth
          disabled={!selectedGift}
        />
      </div>
    </>
  )
}

const GiftShopTab = () => {
  const { data: shop } = useParentShopQuery()
  const { setSelectedGift } = useSendMailModalStore()
  const {
    onClose: closeInventoryGiftShopModal,
    setSelectedTab,
    selectedShopItem,
  } = useInventoryGiftShopModalStore()
  const { isOpen: isOpenSendMailModal, onOpen: openSendMailModal } =
    useSendMailModalStore()
  const { user } = useUserQuery()
  const { t } = useTranslation()
  const [refreshLabelText, setRefreshLabelText] = useState<string | null>(null)

  const userCoins = user?.coins ?? 0

  // Check to see if user doesn't have enough coins for ALL items.
  const doesNotHaveEnoughCoins =
    shop?.slots?.every((slot) => slot.shop_item.price > userCoins) ?? true
  useEffect(() => {
    const interval = setInterval(() => {
      if (!shop) {
        setRefreshLabelText(null)
        return
      }

      let diffSeconds = Math.abs(dayjs().diff(dayjs(shop.end_date), "second"))

      const days = Math.floor(diffSeconds / (24 * 60 * 60))
      diffSeconds %= 24 * 60 * 60

      const hours = Math.floor(diffSeconds / (60 * 60))
      diffSeconds %= 60 * 60

      const minutes = Math.floor(diffSeconds / 60)
      const seconds = diffSeconds % 60

      var timeLeft: string = ""
      if (days > 0) timeLeft += ` ${days}d`
      if (hours > 0 || days > 0) timeLeft += ` ${hours}h`
      if (minutes > 0 || hours > 0 || days > 0) timeLeft += ` ${minutes}m`
      timeLeft += ` ${seconds}s`

      setRefreshLabelText(t("MAIL_SHOP_REFRESHES", { time: timeLeft }))
    }, 1000)

    // Cleanup the interval on unmount
    return () => clearInterval(interval)
  }, [shop, t])

  const purchaseShopItemMutation = usePurchaseShopItemMutation()

  const onPurchaseAndAddToMail = async () => {
    if (!selectedShopItem || !user?.id) return
    trackAnalyticEvent(ANALYTIC_EVENTS.PURCHASE_AND_ADD_TO_MAIL)
    const inventoryItem = await purchaseShopItemMutation.mutateAsync({
      shopItem: selectedShopItem,
      userId: user.id,
    })
    setSelectedGift(inventoryItem)
    if (!isOpenSendMailModal) openSendMailModal()
    closeInventoryGiftShopModal()
  }

  const onPurchaseAndGoToInventory = async () => {
    if (!selectedShopItem || !user?.id) return
    trackAnalyticEvent(ANALYTIC_EVENTS.PURCHASE_AND_GO_TO_INVENTORY)
    await purchaseShopItemMutation.mutateAsync({
      shopItem: selectedShopItem,
      userId: user.id,
    })
    setSelectedTab(InventoryGiftShopModalTab.INVENTORY)
  }

  return (
    <>
      <div className="flex flex-col gap-4 p-6 pt-0 overflow-y-auto">
        <div className="flex flex-col gap-4">
          <Typography variant="bodySmall">
            {doesNotHaveEnoughCoins && t("MAIL_NOT_ENOUGH_BALANCE")}
            {t("MAIL_EARN_COINS_DESC")}
          </Typography>

          <div
            className={`flex flex-row justify-start items-center gap-2 w-fit  rounded-md p-2  ${
              doesNotHaveEnoughCoins ? "bg-red-100" : "bg-indigo-100"
            }`}
          >
            <Typography variant="bodySmall" style={{ whiteSpace: "nowrap" }}>
              {t("MAIL_MY_BALANCE")}
            </Typography>

            <div className="flex flex-row justify-start items-center gap-1">
              <img
                src="/images/icons/coin-icon.png"
                alt="coin"
                style={{ width: "16px" }}
              />
              <Typography variant="bodySmall">{user?.coins}</Typography>
            </div>
          </div>

          {refreshLabelText && (
            <Typography
              variant="bodySmallBold"
              color={JoonUIColor.text.primaryAccent}
              textAlign="center"
            >
              {refreshLabelText}
            </Typography>
          )}

          <div className="flex flex-1 flex-col gap-2 pb-4">
            {!shop?.slots || shop.slots.length === 0 ? (
              <div className="flex flex-row items-center justify-center w-full p-2">
                <Typography
                  variant="bodySmall"
                  color={JoonUIColor.text.secondary}
                >
                  {t("MAIL_NO_ITEMS")}
                </Typography>
              </div>
            ) : (
              shop.slots.map((shopSlot) => (
                <ShopItem
                  key={shopSlot.shop_item.id}
                  shopItem={shopSlot.shop_item}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {selectedShopItem && (
        <div className="flex flex-col gap-2 w-full h-fit px-6 py-4 border-t border-borderPrimary bg-neutral-0 ">
          <Button
            text={
              isOpenSendMailModal
                ? t("MAIL_PURCHASE_ADD_MAIL")
                : t("MAIL_PURCHASE_ADD_NEW_MAIL")
            }
            onClick={onPurchaseAndAddToMail}
            fullWidth
            disabled={!selectedShopItem}
            isLoading={purchaseShopItemMutation.isLoading}
          />
          <Button
            text={t("MAIL_PURCHASE_TO_INVENTORY")}
            onClick={onPurchaseAndGoToInventory}
            fullWidth
            disabled={!selectedShopItem}
            isLoading={purchaseShopItemMutation.isLoading}
            buttonType="secondary"
          />
        </div>
      )}
    </>
  )
}

export default InventoryGiftShopModal
